import { STAGE3_CATEGORY_ANSWERS } from './stage3CategoryAnswers';

const BLOCKED_JUNK_WORDS = new Set([
  'no', 'none', 'nothing', 'na', 'n/a', 'nil', 'null', 'idk', 'dunno', 'unknown', 'skip',
  'pass', 'test', 'testing', 'asdf', 'qwerty', 'xxx', 'random', 'invalid', 'wrong', 'negative',
  'fake', 'dummy', 'blank', 'empty', 'whatever', 'dontknow', 'idonknow', 'notsure', 'maybe',
  'idontknow', 'noidea', 'nah', 'nope',
]);

const MIN_ANSWER_LENGTH = 2;
const MAX_ANSWER_LENGTH = 100;

export function normalizeStage3Answer(value) {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeStage3MatchKey(value) {
  return normalizeStage3Answer(value).toLowerCase().replace(/[^a-z0-9]/g, '');
}

function normalizeTopicKey(value) {
  return normalizeStage3MatchKey(value);
}

function countLetters(value) {
  const matches = String(value ?? '').match(/[a-zA-Z]/g);
  return matches ? matches.length : 0;
}

function isProperAnswerFormat(word) {
  const trimmed = normalizeStage3Answer(word);
  if (trimmed.length < MIN_ANSWER_LENGTH || trimmed.length > MAX_ANSWER_LENGTH) {
    return false;
  }
  if (countLetters(trimmed) < MIN_ANSWER_LENGTH) {
    return false;
  }
  if (/^\d+$/.test(trimmed)) {
    return false;
  }
  return /^[a-zA-Z0-9][a-zA-Z0-9\s'.-]*$/.test(trimmed);
}

function isBlockedJunkWord(word) {
  const key = normalizeStage3MatchKey(word);
  return BLOCKED_JUNK_WORDS.has(key);
}

function isOtherTopicName(word, promptWord, allTopics) {
  const key = normalizeStage3MatchKey(word);
  const promptKey = normalizeTopicKey(promptWord);
  for (const topic of Array.isArray(allTopics) ? allTopics : []) {
    const topicKey = normalizeTopicKey(topic);
    if (topicKey && topicKey === key && topicKey !== promptKey) {
      return true;
    }
  }
  return false;
}

export function buildCategoryAnswersMap(stage3Items) {
  const map = new Map();
  for (const item of Array.isArray(stage3Items) ? stage3Items : []) {
    if (!item || typeof item !== 'object') continue;
    const topic = normalizeStage3Answer(item.word ?? item.topic ?? item.question);
    const answers = Array.isArray(item.answers) ? item.answers : [];
    if (!topic || !answers.length) continue;
    const set = new Set();
    for (const answer of answers) {
      const key = normalizeStage3MatchKey(answer);
      if (key) set.add(key);
    }
    if (set.size) {
      map.set(normalizeTopicKey(topic), set);
    }
  }
  return map;
}

/** Map of topic -> Set of match keys for answers that must always be rejected. */
export function buildCategoryRejectedMap(stage3Items) {
  const map = new Map();
  for (const item of Array.isArray(stage3Items) ? stage3Items : []) {
    if (!item || typeof item !== 'object') continue;
    const topic = normalizeStage3Answer(item.word ?? item.topic ?? item.question);
    const rejected = Array.isArray(item.rejected_answers)
      ? item.rejected_answers
      : Array.isArray(item.bad_answers)
        ? item.bad_answers
        : [];
    if (!topic || !rejected.length) continue;
    const set = new Set();
    for (const answer of rejected) {
      const key = normalizeStage3MatchKey(answer);
      if (key) set.add(key);
    }
    if (set.size) {
      map.set(normalizeTopicKey(topic), set);
    }
  }
  return map;
}

function isRejectedCategoryAnswer(word, promptWord, rejectedAnswersMap) {
  if (!rejectedAnswersMap) return false;
  const set = rejectedAnswersMap.get(normalizeTopicKey(promptWord));
  if (!set || !set.size) return false;
  return set.has(normalizeStage3MatchKey(word));
}

function getAllowedAnswersForPrompt(promptWord, themeAnswersMap) {
  const promptKey = normalizeTopicKey(promptWord);
  const themeSet = themeAnswersMap?.get(promptKey);
  const builtinSet = STAGE3_CATEGORY_ANSWERS[promptKey];
  if (!themeSet && !builtinSet) {
    return null;
  }
  const merged = new Set();
  if (builtinSet) {
    for (const entry of builtinSet) merged.add(entry);
  }
  if (themeSet) {
    for (const entry of themeSet) merged.add(entry);
  }
  return merged;
}

function isAllowedCategoryAnswer(word, promptWord, themeAnswersMap) {
  const allowed = getAllowedAnswersForPrompt(promptWord, themeAnswersMap);
  if (!allowed) {
    return true;
  }
  const key = normalizeStage3MatchKey(word);
  return allowed.has(key);
}

/** When false, answers are limited to admin/builtin lists — skip external dictionary lookup. */
export function shouldValidateStage3WordWithDictionary(promptWord, themeAnswersMap) {
  return getAllowedAnswersForPrompt(promptWord, themeAnswersMap) == null;
}

export function validateStage3RelatedWord(
  word,
  promptWord,
  { allTopics = [], themeAnswersMap, rejectedAnswersMap } = {}
) {
  const trimmed = normalizeStage3Answer(word);

  if (!trimmed) {
    return { valid: false, message: 'Enter a related word' };
  }

  if (!isProperAnswerFormat(trimmed)) {
    return {
      valid: false,
      message: 'Enter a proper name using letters (at least 2 characters)',
    };
  }

  if (isBlockedJunkWord(trimmed)) {
    return {
      valid: false,
      message: 'That is not a valid answer — enter a real word for this category',
    };
  }

  if (normalizeStage3MatchKey(trimmed) === normalizeTopicKey(promptWord)) {
    return {
      valid: false,
      message: 'Enter a different word from the category prompt',
    };
  }

  if (isOtherTopicName(trimmed, promptWord, allTopics)) {
    return {
      valid: false,
      message: 'Enter an answer for this category, not another category name',
    };
  }

  if (isRejectedCategoryAnswer(trimmed, promptWord, rejectedAnswersMap)) {
    return {
      valid: false,
      message: `"${trimmed}" is not accepted for ${promptWord}. Try a different answer.`,
    };
  }

  if (!isAllowedCategoryAnswer(trimmed, promptWord, themeAnswersMap)) {
    return {
      valid: false,
      message: `"${trimmed}" is not a valid ${promptWord} answer. Enter a proper name for this category.`,
    };
  }

  return { valid: true, message: '' };
}

export function countStage3CorrectWords(
  words,
  promptWord,
  { allTopics = [], themeAnswersMap, rejectedAnswersMap } = {}
) {
  const list = Array.isArray(words) ? words : [];
  let count = 0;
  for (const word of list) {
    if (
      validateStage3RelatedWord(word, promptWord, {
        allTopics,
        themeAnswersMap,
        rejectedAnswersMap,
      }).valid
    ) {
      count += 1;
    }
  }
  return count;
}
